import { Input, LuIcon, Menu } from "@overmap-ai/blocks"
import { fieldDataMapping, type StringField } from "@overmap-ai/forms"
import { use, useCallback } from "react"

import { TextInputPopover } from "../../../components/Popovers/TextInputPopover"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { StringFieldFilterDef } from "./StringFieldFilterDef"

interface StringFieldFilterCellProps extends FilterDefRenderProps<StringFieldFilterDef> {
	field: StringField
}

export const StringFieldFilterCell = (props: StringFieldFilterCellProps) => {
	const { field, filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)

	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const handleSelectEmpty = useCallback(() => {
		filter.setFilterValue(null)
	}, [filter])

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsModifierFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton className="truncate">
								{equalsModifierFilterValue !== undefined
									? (equalsModifierFilterValue ?? "empty")
									: "..."}
							</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.Pages defaultPage="initial">
								<Menu.PageContent page="initial">
									<Menu.PageTrigger page="custom">Custom value</Menu.PageTrigger>
									<Menu.Item onSelect={handleSelectEmpty}>empty</Menu.Item>
								</Menu.PageContent>
								<Menu.PageContent page="custom">
									<Input.Root accentColor="base" size={size}>
										<Input.Field
											placeholder="Enter some text"
											type="text"
											value={equalsModifierFilterValue ?? ""}
											onChange={(e) => {
												const value = e.target.value
												filter.setFilterValue(value)
											}}
										/>
									</Input.Root>
								</Menu.PageContent>
							</Menu.Pages>
						</Menu.Content>
					</Menu.Root>
				)
			}
			case "includes":
			case "excludes": {
				return (
					<TextInputPopover value={filterValue ?? ""} onValueChange={filter.setFilterValue} size={size}>
						<FilterCellButton className="shrink-1 min-w-0 grow">
							<span className="truncate">{filterValue || "..."}</span>
						</FilterCellButton>
					</TextInputPopover>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge className="shrink-1 min-w-0">
				<LuIcon icon={fieldDataMapping[field.type].icon} />
				<span className="truncate">{field.label}</span>
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
